import { useEffect, useEffectEvent, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

// Pages that turn under the reader's finger, the way the album storybook's book does (components/storybook/Book.tsx):
// a page follows the drag, turns if it was dragged far enough and falls back if not, and tapping the outer edge of a
// page turns it too.
//
// One page at a time (`spread` off), each page lies on the next and falls away to the left as it turns. As a spread,
// the book lies open: each leaf has a page on its front (a right-hand page) and one on its back (the next left-hand
// page), and turns right over about the spine. `back` is a leaf's back; `under` is what lies under the first leaf on
// the left (nothing, for a cover, and then the closed book sits centered).
export type Leaf = { front: ReactNode; back?: ReactNode; label: string }

// How much of a page's width the reader must drag it to turn it, and the leaves kept drawn either side of the open
// one (the rest of a long book isn't drawn until it's near).
const ENOUGH = 0.2
const NEAR = 2

export function PageTurn({
  leaves,
  open,
  onTurn,
  spread = false,
  under,
  className,
}: {
  leaves: Leaf[]
  open: number
  onTurn: (to: number) => void
  spread?: boolean
  under?: ReactNode
  className?: string
}) {
  // A page alone falls a little past upright, so it's gone; a leaf of a spread turns right over.
  const gone = spread ? -180 : -100
  const last = leaves.length - 1
  const book = useRef<HTMLDivElement>(null)
  const els = useRef(new Map<number, HTMLElement>())
  // The leaf turned last lies on top of whichever side it came to rest on, and stays on top while it turns.
  const [lifted, setLifted] = useState<number | null>(null)
  const n = leaves.length
  const z = (index: number) => (index === lifted ? 3 * n : index < open ? index : 2 * n - index)

  const lay = (index: number, degrees: number, turning: boolean) => {
    const el = els.current.get(index)
    if (!el) return
    el.classList.toggle('turning', turning)
    el.style.transform = `rotateY(${degrees}deg)`
    // The page darkens as it lifts off the paper, and a leaf of a spread lightens again as it lands on the other side.
    const lift = Math.abs(degrees)
    const shade = spread ? Math.sin((lift * Math.PI) / 180) : Math.min(1, lift / 90)
    for (const gloss of el.querySelectorAll<HTMLElement>('.pt-gloss')) gloss.style.opacity = String(shade)
  }
  const go = (to: number) => {
    const next = Math.max(0, Math.min(last, to))
    if (next === open) return
    setLifted(next > open ? next - 1 : next)
    onTurn(next)
  }

  // Every leaf before the open one has turned; the open one and those after lie flat.
  const layAll = useEffectEvent(() => {
    for (const index of els.current.keys()) lay(index, index < open ? gone : 0, true)
  })
  useEffect(() => layAll(), [open, leaves.length])

  const drag = useRef<{ x: number; dx: number; moved: boolean } | null>(null)
  // A page dragged over a link doesn't follow the link when it's let go.
  const dragged = useRef(false)
  const width = () => (book.current?.clientWidth ?? 1) / (spread ? 2 : 1)
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return
    dragged.current = false
    drag.current = { x: e.clientX, dx: 0, moved: false }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current
    if (!d) return
    d.dx = e.clientX - d.x
    if (!d.moved && Math.abs(d.dx) > 6) {
      d.moved = true
      setLifted(d.dx < 0 ? open : open - 1)
      // Keep following a mouse that leaves the book mid-turn.
      book.current?.setPointerCapture(e.pointerId)
    }
    if (!d.moved) return
    const reach = spread ? 180 : 140
    if (d.dx < 0 && open < last) lay(open, Math.max(gone, (d.dx / width()) * reach), false)
    if (d.dx > 0 && open > 0) lay(open - 1, Math.min(0, gone + (d.dx / width()) * reach), false)
  }
  const settle = () => {
    lay(open, 0, true)
    if (open > 0) lay(open - 1, gone, true)
  }
  const onPointerUp = (e: React.PointerEvent) => {
    const d = drag.current
    drag.current = null
    if (!d || !book.current) return
    const w = width()
    if (d.moved) {
      dragged.current = true
      if (d.dx < -w * ENOUGH && open < last) go(open + 1)
      else if (d.dx > w * ENOUGH && open > 0) go(open - 1)
      else settle()
      return
    }
    // A tap on the outer edge of a page turns it; anything that can be pressed is left to do its own thing.
    if ((e.target as HTMLElement).closest('a, button, input, [data-no-turn]')) return
    const box = book.current.getBoundingClientRect()
    const x = (e.clientX - box.left) / box.width
    if (x >= 0.88) go(open + 1)
    else if (x <= 0.12) go(open - 1)
  }

  return (
    <div
      ref={book}
      className={cn(
        'pt-book',
        spread && 'is-spread',
        spread && open === 0 && under === undefined && 'is-closed',
        className,
      )}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onDragStart={(e) => e.preventDefault()}
      onClickCapture={(e) => {
        if (!dragged.current) return
        e.preventDefault()
        e.stopPropagation()
      }}
      onPointerCancel={() => {
        if (drag.current?.moved) settle()
        drag.current = null
      }}
    >
      {spread && under !== undefined && <div className="pt-under">{under}</div>}
      {leaves.map((leaf, index) =>
        Math.abs(index - open) > NEAR ? null : (
          <section
            key={index}
            ref={(el) => {
              if (el) els.current.set(index, el)
              else els.current.delete(index)
            }}
            className="pt-leaf"
            style={
              {
                zIndex: z(index),
                transform: `rotateY(${index < open ? gone : 0}deg)`,
                visibility: index < open - 1 || index > open + 1 ? 'hidden' : undefined,
              } as CSSProperties
            }
            aria-hidden={index !== open}
            inert={index !== open}
            aria-label={leaf.label}
          >
            <div className="pt-face">
              {leaf.front}
              <span className="pt-gloss" />
            </div>
            {spread && (
              <div className="pt-face pt-back">
                {leaf.back}
                <span className="pt-gloss" />
              </div>
            )}
          </section>
        ),
      )}
    </div>
  )
}
